import React, {
  useEffect,
  useState,
  useCallback,
  useRef
} from "react";

import {
  TextField,
  MenuItem,
  Typography,
  Box,
  IconButton,
  Collapse,
  CircularProgress
} from "@mui/material";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import {
  useForm,
  Controller
} from "react-hook-form";

import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

import {
  getEmployees
} from "../../services/employeeService";

import {
  getCategories
} from "../../services/categoryService";

import {
  getPriorities
} from "../../services/priorityService";

import {
  getProjects
} from "../../services/projectService";

import {
  getComments,
  createComment
} from "../../services/commentService";

import { useUserRole } from "../../context/UserRoleContext";
import CommentThread from "../common/CommentThread";

const quillModules = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ["bold", "italic", "underline", "strike"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link", "image"],
    ["clean"]
  ],
  imageResize: {
    modules: ["Resize", "DisplaySize"]
  }
};

const quillFormats = [
  "header",
  "bold",
  "italic",
  "underline",
  "strike",
  "list",
  "bullet",
  "link",
  "image",
  "width"
];

const TICKET_TYPES = ["Task", "Bug", "Story"];

function TicketForm({
  defaultValues,
  onSubmit,
  submitRef,
  onValidityChange
}) {
  const {
    register,
    handleSubmit,
    reset,
    control,
    trigger,
    watch,
    formState: { errors, isValid, touchedFields, isSubmitted }
  } = useForm({ mode: "onChange" });

  const [employees, setEmployees] = useState([]);
  const [categories, setCategories] = useState([]);
  const [priorities, setPriorities] = useState([]);
  const [projects, setProjects] = useState([]);
  const [detailsOpen, setDetailsOpen] = useState(true);
  const [moreFieldsOpen, setMoreFieldsOpen] = useState(true);

  const { employee } = useUserRole();

  const quillRef = useRef(null);

  const loadMasterData = useCallback(async () => {
    // Load each list independently so one failing endpoint doesn't leave
    // the others stuck on "Loading..." forever.
    const [employeeResult, categoryResult, priorityResult, projectResult] = await Promise.allSettled([
      getEmployees(),
      getCategories(),
      getPriorities(),
      getProjects()
    ]);

    if (employeeResult.status === "fulfilled") {
      setEmployees(employeeResult.value);
    } else {
      console.error("Employee Master Data Error:", employeeResult.reason);
    }

    if (categoryResult.status === "fulfilled") {
      setCategories(categoryResult.value);
    } else {
      console.error("Category Master Data Error:", categoryResult.reason);
    }

    if (priorityResult.status === "fulfilled") {
      setPriorities(priorityResult.value);
    } else {
      console.error("Priority Master Data Error:", priorityResult.reason);
    }

    if (projectResult.status === "fulfilled") {
      setProjects(projectResult.value);
    } else {
      console.error("Project Master Data Error:", projectResult.reason);
    }
  }, []);

  useEffect(() => {
    loadMasterData();

    if (defaultValues) {
      reset(defaultValues);
    }

    trigger();
  }, [defaultValues, reset, loadMasterData, trigger]);

  useEffect(() => {
    onValidityChange?.(isValid);
  }, [isValid, onValidityChange]);

  const watchedValues = watch();

  useEffect(() => {
    console.log("[TicketForm] isValid:", isValid);
    console.log("[TicketForm] errors:", errors);
    console.log("[TicketForm] values:", watchedValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isValid, errors, JSON.stringify(watchedValues)]);

  useEffect(() => {
    const editor = quillRef.current?.getEditor();

    if (!editor) {
      return undefined;
    }

    const handlePaste = (event) => {
      const items = event.clipboardData?.items;

      if (!items) {
        return;
      }

      const imageItem = Array.from(items).find((item) => item.type.startsWith("image/"));

      if (!imageItem) {
        return;
      }

      event.preventDefault();

      const file = imageItem.getAsFile();

      if (!file) {
        return;
      }

      const reader = new FileReader();

      reader.onload = () => {
        const range = editor.getSelection(true);
        editor.insertEmbed(range ? range.index : editor.getLength(), "image", reader.result, "user");
      };

      reader.readAsDataURL(file);
    };

    const editorRoot = editor.root;
    editorRoot.addEventListener("paste", handlePaste);

    return () => {
      editorRoot.removeEventListener("paste", handlePaste);
    };
  }, []);

  const handleFormSubmit = useCallback((data) => {
    const payload = {
      title: data.title,
      ticket_type: data.ticket_type || "Task",
      description: data.description,
      category_id: Number(data.category_id),
      priority_id: Number(data.priority_id),
      // New tickets always start Open; edits preserve the current status
      status_id: defaultValues?.status_id || 5,
      assigned_to: data.assigned_to ? Number(data.assigned_to) : null,
      project_id: data.project_id ? Number(data.project_id) : null,
      created_by: data.reporter_id ? Number(data.reporter_id) : null
    };

    onSubmit(payload);
  }, [onSubmit, defaultValues]);

  useEffect(() => {
    if (submitRef) {
      submitRef.current = () => {
        handleSubmit(handleFormSubmit)();
      };
    }
  }, [submitRef, handleSubmit, handleFormSubmit]);

  return (
    <form style={{ height: "100%" }}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "minmax(0, 4fr) minmax(0, 1fr)" },
          gap: 3,
          height: "100%"
        }}
      >
        {/* Left column: Title + Description */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.2 }}>
          <Box>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: "text.primary", mb: 0.8 }}>
              Title <Box component="span" sx={{ color: "#d32f2f" }}>*</Box>
            </Typography>

            <TextField
              fullWidth
              size="small"
              placeholder="Enter ticket title"
              error={Boolean(errors.title) && (touchedFields.title || isSubmitted)}
              helperText={errors.title && (touchedFields.title || isSubmitted) ? "Title is required" : ""}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  bgcolor: "background.paper"
                },
                "& .MuiFormHelperText-root": {
                  minHeight: 20,
                  mt: 0.7
                }
              }}
              {...register("title", { required: true })}
            />
          </Box>

          <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: "text.primary", mb: 0.8 }}>
              Description <Box component="span" sx={{ color: "#d32f2f" }}>*</Box>
            </Typography>

            <Box
              sx={(theme) => ({
                flex: 1,
                display: "flex",
                flexDirection: "column",
                "& .quill": { display: "flex", flexDirection: "column", flex: 1 },
                "& .ql-toolbar": {
                  borderColor: theme.palette.divider,
                  bgcolor: "background.paper",
                  borderTopLeftRadius: 8,
                  borderTopRightRadius: 8,
                },
                "& .ql-container": {
                  flex: 1,
                  overflowY: "auto",
                  borderColor: theme.palette.divider,
                  borderBottomLeftRadius: 8,
                  borderBottomRightRadius: 8,
                },
                "& .ql-editor": {
                  bgcolor: "background.paper",
                  color: "text.primary",
                },
                "& .ql-editor.ql-blank::before": {
                  color: theme.palette.text.secondary,
                  fontStyle: "normal",
                },
                "& .ql-picker-label": {
                  color: theme.palette.text.primary,
                },
                "& .ql-picker-options": {
                  bgcolor: "background.paper",
                  color: "text.primary",
                },
                "& .ql-stroke": {
                  stroke: theme.palette.text.secondary,
                },
                "& .ql-fill": {
                  fill: theme.palette.text.secondary,
                },
              })}
            >
              <Controller
                name="description"
                control={control}
                defaultValue={defaultValues?.description || ""}
                rules={{ required: true }}
                render={({ field }) => (
                  <ReactQuill
                    ref={quillRef}
                    className="ticket-description-editor"
                    value={field.value}
                    onChange={field.onChange}
                    modules={quillModules}
                    formats={quillFormats}
                    placeholder="Enter your Description (min 100 words)"
                    style={{
                      fontSize: 16
                    }}
                  />
                )}
              />
            </Box>

            {errors.description && (touchedFields.description || isSubmitted) && (
              <Typography sx={{ fontSize: 12, color: "#d32f2f", mt: 0.7, ml: 1.7 }}>
                Description is required
              </Typography>
            )}
          </Box>

          {defaultValues?.ticket_id ? (
            <CommentThread
              key={`ticket-${defaultValues.ticket_id}`}
              entityId={defaultValues.ticket_id}
              members={employees}
              currentEmployeeId={employee?.employee_id}
              currentEmployeeName={employee?.employee_name}
              fetchComments={getComments}
              postComment={createComment}
            />
          ) : (
            <Box>
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: "text.primary", mb: 0.8 }}>
                Activity
              </Typography>
              <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                Comments will be available once this ticket is created.
              </Typography>
            </Box>
          )}
        </Box>

        {/* Right column: Details (Category, Priority) + More Fields (Assign To, Project) */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.2 }}>
          <Box sx={{ bgcolor: "background.paper", border: "1px solid", borderColor: "divider", borderRadius: 2, overflow: "hidden" }}>
            <Box
              onClick={() => setDetailsOpen((prev) => !prev)}
              sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2, py: 1.4, cursor: "pointer" }}
            >
              <Typography sx={{ fontSize: 11, fontWeight: 800, color: "text.secondary", textTransform: "uppercase", letterSpacing: 0.5 }}>
                Details
              </Typography>
              <IconButton size="small">
                <ExpandMoreIcon
                  fontSize="small"
                  sx={{ transform: detailsOpen ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.15s ease" }}
                />
              </IconButton>
            </Box>

            <Collapse in={detailsOpen}>
              <Box sx={{ px: 2, pb: 2, display: "flex", flexDirection: "column", gap: 2 }}>
                <Box>
                  <Typography sx={{ fontSize: 13, fontWeight: 700, color: "text.primary", mb: 0.8 }}>
                    Type <Box component="span" sx={{ color: "#d32f2f" }}>*</Box>
                  </Typography>

                  <Controller
                    name="ticket_type"
                    control={control}
                    defaultValue={defaultValues?.ticket_type || "Task"}
                    rules={{ required: true }}
                    render={({ field }) => (
                      <TextField
                        select
                        fullWidth
                        size="small"
                        value={field.value ?? "Task"}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        inputRef={field.ref}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                            bgcolor: "background.paper"
                          },
                          "& .MuiFormHelperText-root": {
                            minHeight: 20,
                            mt: 0.7
                          }
                        }}
                      >
                        {TICKET_TYPES.map((type) => (
                          <MenuItem key={type} value={type}>
                            {type}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  />
                </Box>

                <Box>
                  <Typography sx={{ fontSize: 13, fontWeight: 700, color: "text.primary", mb: 0.8 }}>
                    Category <Box component="span" sx={{ color: "#d32f2f" }}>*</Box>
                  </Typography>

                  <Controller
                    name="category_id"
                    control={control}
                    defaultValue={defaultValues?.category_id || ""}
                    rules={{ required: true }}
                    render={({ field }) => (
                      <Box display="flex" alignItems="center" gap={1}>
                      <TextField
                        select
                        fullWidth
                        size="small"
                        disabled={categories.length === 0}
                        value={field.value ?? ""}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        inputRef={field.ref}
                        error={Boolean(errors.category_id) && (touchedFields.category_id || isSubmitted)}
                        helperText={errors.category_id && (touchedFields.category_id || isSubmitted) ? "Please select a category" : ""}
                        SelectProps={{ displayEmpty: true }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                            bgcolor: "background.paper"
                          },
                          "& .MuiFormHelperText-root": {
                            minHeight: 20,
                            mt: 0.7
                          }
                        }}
                      >
                        <MenuItem value="" disabled>
                          {categories.length === 0 ? "Loading categories..." : "Select category"}
                        </MenuItem>

                        {categories.map((item) => (
                          <MenuItem
                            key={item.category_id}
                            value={item.category_id}
                          >
                            {item.category_name}
                          </MenuItem>
                        ))}
                      </TextField>
                      {categories.length === 0 && <CircularProgress size={18} sx={{ flexShrink: 0 }} />}
                      </Box>
                    )}
                  />
                </Box>

                <Box>
                  <Typography sx={{ fontSize: 13, fontWeight: 700, color: "text.primary", mb: 0.8 }}>
                    Priority <Box component="span" sx={{ color: "#d32f2f" }}>*</Box>
                  </Typography>

                  <Controller
                    name="priority_id"
                    control={control}
                    defaultValue={defaultValues?.priority_id || ""}
                    rules={{ required: true }}
                    render={({ field }) => (
                      <Box display="flex" alignItems="center" gap={1}>
                      <TextField
                        select
                        fullWidth
                        size="small"
                        disabled={priorities.length === 0}
                        value={field.value ?? ""}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        inputRef={field.ref}
                        error={Boolean(errors.priority_id) && (touchedFields.priority_id || isSubmitted)}
                        helperText={errors.priority_id && (touchedFields.priority_id || isSubmitted) ? "Please select a priority" : ""}
                        SelectProps={{ displayEmpty: true }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                            bgcolor: "background.paper"
                          },
                          "& .MuiFormHelperText-root": {
                            minHeight: 20,
                            mt: 0.7
                          }
                        }}
                      >
                        <MenuItem value="" disabled>
                          {priorities.length === 0 ? "Loading priorities..." : "Select priority"}
                        </MenuItem>

                        {priorities.map((item) => (
                          <MenuItem
                            key={item.priority_id}
                            value={item.priority_id}
                          >
                            {item.priority_name}
                          </MenuItem>
                        ))}
                      </TextField>
                      {priorities.length === 0 && <CircularProgress size={18} sx={{ flexShrink: 0 }} />}
                      </Box>
                    )}
                  />
                </Box>
              </Box>
            </Collapse>
          </Box>

          <Box sx={{ bgcolor: "background.paper", border: "1px solid", borderColor: "divider", borderRadius: 2, overflow: "hidden" }}>
            <Box
              onClick={() => setMoreFieldsOpen((prev) => !prev)}
              sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2, py: 1.4, cursor: "pointer" }}
            >
              <Typography sx={{ fontSize: 11, fontWeight: 800, color: "text.secondary", textTransform: "uppercase", letterSpacing: 0.5 }}>
                More Fields
              </Typography>
              <IconButton size="small">
                <ExpandMoreIcon
                  fontSize="small"
                  sx={{ transform: moreFieldsOpen ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.15s ease" }}
                />
              </IconButton>
            </Box>

            <Collapse in={moreFieldsOpen}>
              <Box sx={{ px: 2, pb: 2, display: "flex", flexDirection: "column", gap: 2 }}>
                <Box>
                  <Typography sx={{ fontSize: 13, fontWeight: 700, color: "text.primary", mb: 0.8 }}>
                    Assign To <Box component="span" sx={{ color: "#d32f2f" }}>*</Box>
                  </Typography>

                  <Controller
                    name="assigned_to"
                    control={control}
                    defaultValue={defaultValues?.assigned_to || ""}
                    rules={{ required: true }}
                    render={({ field }) => (
                      <Box display="flex" alignItems="center" gap={1}>
                      <TextField
                        select
                        fullWidth
                        size="small"
                        disabled={employees.length === 0}
                        value={field.value ?? ""}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        inputRef={field.ref}
                        error={Boolean(errors.assigned_to) && (touchedFields.assigned_to || isSubmitted)}
                        helperText={errors.assigned_to && (touchedFields.assigned_to || isSubmitted) ? "Please select an employee" : ""}
                        SelectProps={{ displayEmpty: true }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                            bgcolor: "background.paper"
                          },
                          "& .MuiFormHelperText-root": {
                            minHeight: 20,
                            mt: 0.7
                          }
                        }}
                      >
                        <MenuItem value="" disabled>
                          {employees.length === 0 ? "Loading employees..." : "Select employee"}
                        </MenuItem>

                        {employees.map((employee) => (
                          <MenuItem
                            key={employee.employee_id}
                            value={employee.employee_id}
                          >
                            {employee.employee_name}
                          </MenuItem>
                        ))}
                      </TextField>
                      {employees.length === 0 && <CircularProgress size={18} sx={{ flexShrink: 0 }} />}
                      </Box>
                    )}
                  />
                </Box>

                <Box>
                  <Typography sx={{ fontSize: 13, fontWeight: 700, color: "text.primary", mb: 0.8 }}>
                    Project <Box component="span" sx={{ color: "#d32f2f" }}>*</Box>
                  </Typography>

                  <Controller
                    name="project_id"
                    control={control}
                    defaultValue={defaultValues?.project_id || ""}
                    rules={{ required: true }}
                    render={({ field }) => (
                      <Box display="flex" alignItems="center" gap={1}>
                      <TextField
                        select
                        fullWidth
                        size="small"
                        disabled={projects.length === 0 || Boolean(defaultValues?.ticket_id)}
                        value={field.value ?? ""}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        inputRef={field.ref}
                        error={Boolean(errors.project_id) && (touchedFields.project_id || isSubmitted)}
                        helperText={
                          errors.project_id && (touchedFields.project_id || isSubmitted)
                            ? "Please select a project"
                            : defaultValues?.ticket_id
                              ? "Project cannot be changed after a ticket is created"
                              : ""
                        }
                        SelectProps={{ displayEmpty: true }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                            bgcolor: "background.paper"
                          },
                          "& .MuiFormHelperText-root": {
                            minHeight: 20,
                            mt: 0.7
                          }
                        }}
                      >
                        <MenuItem value="" disabled>
                          {projects.length === 0 ? "Loading projects..." : "Select project"}
                        </MenuItem>

                        {projects.map((project) => (
                          <MenuItem
                            key={project.project_id}
                            value={project.project_id}
                          >
                            {project.project_name}
                          </MenuItem>
                        ))}
                      </TextField>
                      {projects.length === 0 && <CircularProgress size={18} sx={{ flexShrink: 0 }} />}
                      </Box>
                    )}
                  />
                </Box>

                <Box>
                  <Typography sx={{ fontSize: 13, fontWeight: 700, color: "text.primary", mb: 0.8 }}>
                    Reporter <Box component="span" sx={{ color: "#d32f2f" }}>*</Box>
                  </Typography>

                  <Controller
                    name="reporter_id"
                    control={control}
                    defaultValue={defaultValues?.created_by_id || employee?.employee_id || ""}
                    rules={{ required: true }}
                    render={({ field }) => (
                      <Box display="flex" alignItems="center" gap={1}>
                      <TextField
                        select
                        fullWidth
                        size="small"
                        disabled={employees.length === 0}
                        value={field.value ?? ""}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        inputRef={field.ref}
                        error={Boolean(errors.reporter_id) && (touchedFields.reporter_id || isSubmitted)}
                        helperText={errors.reporter_id && (touchedFields.reporter_id || isSubmitted) ? "Please select a reporter" : ""}
                        SelectProps={{ displayEmpty: true }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                            bgcolor: "background.paper"
                          },
                          "& .MuiFormHelperText-root": {
                            minHeight: 20,
                            mt: 0.7
                          }
                        }}
                      >
                        <MenuItem value="" disabled>
                          {employees.length === 0 ? "Loading employees..." : "Select reporter"}
                        </MenuItem>

                        {employees.map((reporter) => (
                          <MenuItem
                            key={reporter.employee_id}
                            value={reporter.employee_id}
                          >
                            {reporter.employee_name}
                          </MenuItem>
                        ))}
                      </TextField>
                      {employees.length === 0 && <CircularProgress size={18} sx={{ flexShrink: 0 }} />}
                      </Box>
                    )}
                  />
                </Box>
              </Box>
            </Collapse>
          </Box>
        </Box>
      </Box>
    </form>
  );
}

export default TicketForm;
